import React, { useState, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { Translate } from 'react-localize-redux';

import { PERFORMANCE_COLORS } from '../../consts';
import { SunburstNodeType } from '../../types';
import css from './SunburstChart.css';

const TRANSLATION_PREFIX = 'STATIC.PAGES.ANOMALY_ANALYSIS';
const SIZE = 500;
const RADIUS = SIZE / 2;
const INNER_RADIUS = 0.25; // ratio of RADIUS for hollow center
const RING_GAP = 0.02; // gap between rings as ratio of RADIUS
const MIN_LABEL_ARC_LENGTH = 35;
const TWO_PI = 2 * Math.PI;

// Builds an SVG donut-arc path: outer arc clockwise from startAngle to endAngle, inner arc back counter-clockwise.
// Angles are in radians clockwise from north; innerRadius/outerRadius are fractions of RADIUS.
const makeArcPath = (startAngle, endAngle, innerRadius, outerRadius) => {
    const innerR = innerRadius * RADIUS;
    const outerR = outerRadius * RADIUS;

    const sinStart = Math.sin(startAngle);
    const cosStart = Math.cos(startAngle);

    if (endAngle - startAngle >= TWO_PI) {
        const midAngle = startAngle + Math.PI;
        const sinMiddle = Math.sin(midAngle);
        const cosMiddle = Math.cos(midAngle);
        return [
            `M ${outerR * sinStart} ${-outerR * cosStart}`,
            `A ${outerR} ${outerR} 0 1 1 ${outerR * sinMiddle} ${-outerR * cosMiddle}`,
            `A ${outerR} ${outerR} 0 1 1 ${outerR * sinStart} ${-outerR * cosStart}`,
            `L ${innerR * sinStart} ${-innerR * cosStart}`,
            `A ${innerR} ${innerR} 0 1 0 ${innerR * sinMiddle} ${-innerR * cosMiddle}`,
            `A ${innerR} ${innerR} 0 1 0 ${innerR * sinStart} ${-innerR * cosStart}`,
            'Z',
        ].join(' ');
    }

    const sinEnd = Math.sin(endAngle);
    const cosEnd = Math.cos(endAngle);
    const largeArc = endAngle - startAngle > Math.PI ? 1 : 0;

    return [
        `M ${outerR * sinStart} ${-outerR * cosStart}`,
        `A ${outerR} ${outerR} 0 ${largeArc} 1 ${outerR * sinEnd} ${-outerR * cosEnd}`,
        `L ${innerR * sinEnd} ${-innerR * cosEnd}`,
        `A ${innerR} ${innerR} 0 ${largeArc} 0 ${innerR * sinStart} ${-innerR * cosStart}`,
        'Z',
    ].join(' ');
};

const CHAR_WIDTH_ESTIMATE = 6; // px per character at font-size 11
const ELLIPSIS = '…';
const LABEL_PADDING = 12; // horizontal space reserved on each side of the label

// Truncates name to fit arcWidth: maxChars = floor(px / charWidth), appends ellipsis if cut.
const truncateLabel = (name, arcWidth) => {
    const maxChars = Math.floor(arcWidth / CHAR_WIDTH_ESTIMATE);
    return maxChars >= name.length ? name : maxChars <= 1 ? ELLIPSIS : name.slice(0, maxChars - 1) + ELLIPSIS;
};

// Positions the label at the arc midpoint, subtracting 90° to align text tangentially
// and flipping 180° when the result would be upside-down (deg in 90°–270°).
const getLabelTransform = (midAngle, midRadius) => {
    const x = Math.sin(midAngle) * midRadius;
    const y = -Math.cos(midAngle) * midRadius;
    const deg = ((((midAngle * 180) / Math.PI - 90) % 360) + 360) % 360;
    const flip = deg > 90 && deg < 270 ? 180 : 0;
    return `translate(${x},${y}) rotate(${deg + flip})`;
};

const ArcLabel = ({ name, startAngle, endAngle, innerRadius, outerRadius }) => {
    const midAngle = (startAngle + endAngle) / 2;
    const midRadius = ((innerRadius + outerRadius) / 2) * RADIUS;
    const arcWidth = (endAngle - startAngle) * midRadius;
    if (arcWidth <= MIN_LABEL_ARC_LENGTH) return null;
    const label = truncateLabel(name, arcWidth - LABEL_PADDING);
    return (
        <text
            transform={getLabelTransform(midAngle, midRadius)}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize={11}
            fill="#fff"
            pointerEvents="none"
            style={{ userSelect: 'none' }}
        >
            {label}
        </text>
    );
};

ArcLabel.propTypes = {
    name: PropTypes.string.isRequired,
    startAngle: PropTypes.number.isRequired,
    endAngle: PropTypes.number.isRequired,
    innerRadius: PropTypes.number.isRequired,
    outerRadius: PropTypes.number.isRequired,
};

// Build ring layout: groups nodes by ringIndex, computes angle slices per ring
const buildRings = (nodes, search) => {
    const ringsByIndex = nodes.reduce((acc, node) => {
        (acc[node.ringIndex] ??= []).push(node);
        return acc;
    }, {});

    const allRings = Object.values(ringsByIndex);
    const ringCount = allRings.length;
    if (!ringCount) return [];

    const lowerSearch = search?.toLowerCase();
    const ringWidth = (1 - INNER_RADIUS - RING_GAP * (ringCount - 1)) / ringCount;

    return allRings.flatMap((ringNodes, position) => {
        const innerRadius = INNER_RADIUS + position * (ringWidth + RING_GAP);
        const outerRadius = innerRadius + ringWidth;

        const filteredNodes = lowerSearch
            ? ringNodes.filter(node => node.name?.toLowerCase().includes(lowerSearch))
            : ringNodes;
        const totalValue = filteredNodes.reduce((sum, node) => sum + (node.value || 0), 0);
        if (!totalValue) return [];

        const result = filteredNodes.reduce(
            ({ slices, currentAngle }, node) => {
                const sweep = (node.value / totalValue) * TWO_PI;
                slices.push({
                    node,
                    startAngle: currentAngle,
                    endAngle: currentAngle + sweep,
                    innerRadius,
                    outerRadius,
                });
                return { slices, currentAngle: currentAngle + sweep };
            },
            { slices: [], currentAngle: 0 }
        );
        return result.slices;
    });
};

const SunburstChart = ({ nodes, search }) => {
    const [tooltip, setTooltip] = useState(null);
    const containerRef = useRef(null);

    const rings = useMemo(() => buildRings(nodes, search), [nodes, search]);

    if (!rings.length) {
        return <Translate id={`${TRANSLATION_PREFIX}.SUNBURST.NO_RESULTS`} />;
    }

    return (
        <div ref={containerRef} className={css.chartWrapper}>
            <svg viewBox={`${-RADIUS} ${-RADIUS} ${SIZE} ${SIZE}`} width="100%" height="100%">
                <circle r={INNER_RADIUS * RADIUS} fill="white" />
                {rings.map(({ node, startAngle, endAngle, innerRadius, outerRadius }) => {
                    const arcPath = makeArcPath(startAngle, endAngle, innerRadius, outerRadius);
                    return (
                        <g
                            key={node.id}
                            onMouseMove={event => {
                                const rect = containerRef.current?.getBoundingClientRect();
                                if (!rect) return;
                                setTooltip({
                                    x: event.clientX - rect.left,
                                    y: event.clientY - rect.top,
                                    name: node.name,
                                });
                            }}
                            onMouseLeave={() => setTooltip(null)}
                        >
                            <path
                                d={arcPath}
                                fill={node.color || PERFORMANCE_COLORS.NO_DATA}
                                stroke={endAngle - startAngle < TWO_PI ? '#fff' : 'none'}
                                strokeWidth={1}
                            />
                            <ArcLabel
                                name={node.name}
                                startAngle={startAngle}
                                endAngle={endAngle}
                                innerRadius={innerRadius}
                                outerRadius={outerRadius}
                            />
                        </g>
                    );
                })}
            </svg>
            {tooltip && (
                <div className={css.tooltip} style={{ left: tooltip.x, top: tooltip.y + 12 }}>
                    {tooltip.name}
                </div>
            )}
        </div>
    );
};

SunburstChart.propTypes = {
    nodes: PropTypes.arrayOf(SunburstNodeType),
    search: PropTypes.string,
};

export default SunburstChart;
